import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Users, Truck, CheckCircle, XCircle, DollarSign, TrendingUp, TrendingDown,
  Package, Building, AlertTriangle, Activity, BarChart3, PieChartIcon,
  Download, FileSpreadsheet, FileText, Calendar, Filter, RefreshCw,
  ChevronDown, X, Loader2, Target, Percent, UserPlus, UserMinus,
  Repeat, Wallet, MapPin, Crown, AlertCircle, ArrowUpRight, ArrowDownRight,
  Gauge, GitBranch, Circle, Wifi, WifiOff, User, Clock, Search, ClipboardList
} from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area,
  ComposedChart, RadialBarChart, RadialBar, FunnelChart, Funnel, LabelList,
  Treemap, ReferenceLine
} from 'recharts';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeNotifications } from '../../hooks/useRealtimeNotifications';
import OperationalKPIs from '../../components/admin/OperationalKPIs';
import FinancialKPIs from '../../components/admin/FinancialKPIs';
import RecouvrementKPIs from '../../components/admin/RecouvrementKPIs';
import ObjectivesKPIDashboard from '../../components/admin/ObjectivesKPIDashboard';
import BillingAlertBanner from '../../components/admin/BillingAlertBanner';
import WorkflowDivergenceCounter from '../../components/admin/WorkflowDivergenceCounter';
import ProfitabilityKPIs from '../../components/admin/ProfitabilityKPIs';
import FleetKPIs from '../../components/admin/FleetKPIs';
import OperationalKpisSearch from './OperationalKpis';
import { logger } from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Couleurs pour les graphiques
const COLORS = ['#1db584', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];
const SECTOR_COLORS = {
  'Agriculture': '#22c55e',
  'Agroalimentaire & Transformation': '#84cc16',
  'BTP & Construction': '#f59e0b',
  'Commerce general': '#3b82f6',
  'Mines & Extraction': '#ef4444',
  'Industrie legere & Manufacture': '#8b5cf6',
  'Transport & Logistique': '#1db584',
  'Sante & Pharmaceutique': '#ec4899',
  'Conseil & Services aux entreprises': '#06b6d4',
  'Telecommunications & IT': '#6366f1',
  'Textile & Habillement': '#f97316',
  'Cacao, Cafe & Cultures d\'exportation': '#a3e635',
  'Grande distribution & Supermarches': '#0ea5e9',
  'Transit & Douanes': '#14b8a6',
  'Banque & Finance': '#eab308',
  'Import / Export': '#f472b6',
  'Non Defini': '#d4d4d4',
  'non_defini': '#d4d4d4'
};

// Sector labels now come directly from backend as sector_name (display-ready)
// This mapping is only used as a fallback for legacy data
const SECTOR_LABELS = {
  'non_defini': 'Non Defini',
  'Non Defini': 'Non Defini'
};

const PERIOD_OPTIONS = [
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
  { value: '90d', label: '90 derniers jours' },
  { value: '12m', label: '12 derniers mois' },
  { value: 'ytd', label: 'Année en cours' },
  { value: 'all', label: 'Tout' }
];

const OPERATION_TYPE_OPTIONS = [
  { value: 'all', label: 'Tous types' },
  { value: 'intraland', label: 'Intraland' },
  { value: 'interland', label: 'Interland' },
  { value: 'import', label: 'Import' },
  { value: 'export', label: 'Export' }
];

// Roles allowed to see strategic KPIs
const STRATEGIC_KPI_ROLES = ['admin', 'super_admin', 'direction', 'finance'];
// RBAC V2.3 : les onglets KPI Financiers / Recouvrement / Rentabilité sont réservés à
// admin/finance/direction/auditeur. Les ops (lead_ops, ops, field) ne doivent voir
// que l'onglet KPIs Opérationnels.
const FINANCIAL_TABS_ROLES = ['admin', 'super_admin', 'direction', 'finance', 'auditeur'];

// Formatter pour les montants (compact - axes/labels)
const formatAmount = (value) => {
  if (value >= 1000000000) return `${(value / 1000000000).toFixed(1)}Md`;
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value?.toLocaleString('fr-FR') || '0';
};

// Formatter exact (KPI cards & tooltips - precision)
const formatExact = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Math.round(value).toLocaleString('fr-FR');
};

// Format percentage with sign
const formatGrowth = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'Nouveau';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

// Export CSV function
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(';'),
    ...data.map(row => headers.map(h => {
      const val = row[h];
      if (typeof val === 'string' && val.includes(';')) return `"${val}"`;
      return val ?? '';
    }).join(';'))
  ].join('\n');
  
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// Export PDF function using html2canvas + jsPDF for proper rendering
const exportToPDF = async (elementId, title, setExporting) => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error('PDF Export: Element not found:', elementId);
    throw new Error('Element not found');
  }
  
  if (setExporting) setExporting(true);
  
  try {
    logger.debug('PDF Export: Starting html2canvas capture...');
    
    // Create canvas from the element with optimized settings
    const canvas = await html2canvas(element, {
      scale: 1.5, // Reduced scale for better performance,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: true, // Enable logging for debugging,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: -window.scrollY, // Capture from top,
      onclone: (clonedDoc) => {
        // Ensure all SVG charts are visible
        const clonedElement = clonedDoc.getElementById(elementId);
        if (clonedElement) {
          clonedElement.style.overflow = 'visible';
          clonedElement.style.position = 'relative';
          // Force SVGs to be visible
          const svgs = clonedElement.querySelectorAll('svg');
          svgs.forEach(svg => {
            svg.style.overflow = 'visible';
          });
          // Force Recharts elements to be visible
          const rechartsContainers = clonedElement.querySelectorAll('.recharts-wrapper');
          rechartsContainers.forEach(container => {
            container.style.overflow = 'visible';
          });
        }
      }
    });

    logger.debug('PDF Export: Canvas created, dimensions:', canvas.width, 'x', canvas.height);

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;

    // Calculate PDF dimensions (A4 landscape for better dashboard fit)
    const pdfWidth = 297; // A4 landscape width in mm
    const pdfHeight = 210; // A4 landscape height in mm
    const margin = 10;
    
    // Create PDF
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Add header
    const headerHeight = 20;
    pdf.setFillColor(29, 181, 132); // #1db584
    pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), headerHeight, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin, 13);
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    const dateStr = `Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`;
    pdf.text(dateStr, pdf.internal.pageSize.getWidth() - margin - pdf.getTextWidth(dateStr), 13);
    
    // Calculate dimensions for the image
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const availableHeight = pageHeight - headerHeight - margin - 5;
    const availableWidth = pageWidth - (2 * margin);
    
    // Scale image to fit page
    const imgRatio = imgWidth / imgHeight;
    let finalWidth = availableWidth;
    let finalHeight = finalWidth / imgRatio;
    
    // If too tall, scale down
    if (finalHeight > availableHeight) {
      finalHeight = availableHeight;
      finalWidth = finalHeight * imgRatio;
    }
    
    // Center the image horizontally
    const xOffset = (pageWidth - finalWidth) / 2;
    
    // Add the captured image
    pdf.addImage(imgData, 'PNG', xOffset, headerHeight + 3, finalWidth, finalHeight);
    
    // Add footer
    pdf.setTextColor(128, 128, 128);
    pdf.setFontSize(8);
    pdf.text('Baobab Connect - Tableau de Bord Admin', margin, pageHeight - 5);
    pdf.text(`Page 1`, pageWidth - margin - 15, pageHeight - 5);
    
    // Save PDF
    const fileName = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    logger.debug('PDF Export: Saving PDF as', fileName);
    pdf.save(fileName);
    
    logger.debug('PDF Export: PDF saved successfully');
    
  } catch (error) {
    console.error('PDF Export Error:', error);
    throw error;
  } finally {
    if (setExporting) setExporting(false);
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const exportMenuRef = useRef(null);

  // Lazy-load des onglets KPI : chaque onglet n'est monté qu'après avoir été
  // activé au moins une fois. Une fois monté, il reste en DOM (forceMount) pour
  // éviter un re-fetch au retour. Gain principal : l'arrivée sur le dashboard
  // ne déclenche QUE les fetchs de l'onglet par défaut (pas les 6 autres).
  const [mountedTabs, setMountedTabs] = useState(() => new Set());
  
  // Real-time notifications for status changes
  const { isConnected: wsConnected, lastNotification } = useRealtimeNotifications({
    enabled: true,
    showToasts: true
  });
  
  // Check if user can see strategic KPIs
  const canSeeStrategicKPIs = user && STRATEGIC_KPI_ROLES.includes(user.role);
  const canSeeFinancialTabs = user && FINANCIAL_TABS_ROLES.includes(user.role);
  
  // Commercial role: client filter
  const isCommercial = user?.role === 'commercial';
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all'); // 'all' or client id
  
  // Filters
  const [filters, setFilters] = useState({
    period: '30d',
    sector: 'all',
    operationType: 'all'
  });
  const [activeFilters, setActiveFilters] = useState({
    period: '30d',
    sector: 'all',
    operationType: 'all'
  });
  
  // Basic Stats
  const [basicStats, setBasicStats] = useState({
    totalUsers: 0,
    totalOperations: 0,
    preparation: 0,
    execution: 0,
    operationsEnCours: 0,
    operationsTerminees: 0,
    operationsAnnulees: 0,
    revenuTotal: 0,
    avgOperationValue: 0,
    completionRate: 0
  });
  
  // Strategic KPIs (admin only)
  const [strategicKPIs, setStrategicKPIs] = useState({
    // Growth
    growthMoM: null,
    growthYoY: null,
    currentMonthRevenue: 0,
    previousMonthRevenue: 0,
    currentYearRevenue: 0,
    previousYearRevenue: 0,
    
    // ARPU
    arpu: 0,
    arpuChange: null,
    activeClients: 0,
    
    // Conversion
    conversionRate: 0,
    quotesCreated: 0,
    quotesAccepted: 0,
    
    // Cancellation
    cancellationRate: 0,
    cancelledByClient: 0,
    cancelledByAdmin: 0,
    
    // Acquisition & Retention
    newClientsThisMonth: 0,
    newClientsPrevMonth: 0,
    retentionRate: 0,
    returningClients: 0,
    totalActiveClients: 0,
    churnedClients: 0,
    
    // Pipeline
    pipelineValue: 0,
    pipelineCount: 0,
    avgDealSize: 0,
    
    // Geographic
    intralandCount: 0,
    interlandCount: 0,
    intralandRevenue: 0,
    interlandRevenue: 0,
    
    // Concentration
    top10ClientsRevenue: 0,
    top10ClientsPercent: 0,
    topClients: [],
    
    // Operational KPIs
    totalDistanceKm: 0,
    totalWeightTons: 0,
    totalTrucks: 0,
    totalContainers: 0,
    totalIncidents: 0,
    incidentRate: 0,
    avgDistancePerOp: 0,
    avgWeightPerOp: 0,
    vehicleTypeBreakdown: [],
    cargoTypeBreakdown: []
  });
  
  const [advancedStats, setAdvancedStats] = useState({
    summary: { totalUsers: 0, activeUsers30d: 0, totalCompanies: 0, activeCompanies30d: 0, inactiveCompanies: 0 },
    bySector: [],
    topCompanies: [],
    lowActivityCompanies: [],
    monthlyTrends: []
  });
  const [statusData, setStatusData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [monthlyGrowthData, setMonthlyGrowthData] = useState([]);
  const [objectivesProgress, setObjectivesProgress] = useState(null);

  // Effective tab : calcule le tab réellement affiché (résout le fallback null)
  const effectiveTab = activeTab || (canSeeStrategicKPIs ? 'overview' : 'operational');

  // Marque un onglet comme "monté une fois" dès qu'il devient actif
  useEffect(() => {
    setMountedTabs(prev => {
      if (prev.has(effectiveTab)) return prev;
      const next = new Set(prev);
      next.add(effectiveTab);
      return next;
    });
  }, [effectiveTab]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDateRange = useCallback((period) => {
    const now = new Date();
    let startDate = new Date();
    
    switch (period) {
      case '7d':
        startDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(now.getDate() - 90);
        break;
      case '12m':
        startDate.setMonth(now.getMonth() - 12);
        break;
      case 'ytd':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      case 'all':
      default:
        startDate = new Date(2020, 0, 1);
    }
    
    return { startDate, endDate: now };
  }, []);

  const calculateStrategicKPIs = useCallback((operations, allOperations, users) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    // === GROWTH KPIs ===
    const currentMonthOps = allOperations.filter(op => {
      const d = new Date(op.createdAt);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });
    const previousMonthOps = allOperations.filter(op => {
      const d = new Date(op.createdAt);
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      return d.getMonth() === prevMonth && d.getFullYear() === prevYear;
    });
    const currentYearOps = allOperations.filter(op => {
      const d = new Date(op.createdAt);
      return d.getFullYear() === currentYear;
    });
    const previousYearOps = allOperations.filter(op => {
      const d = new Date(op.createdAt);
      return d.getFullYear() === currentYear - 1;
    });
    
    const currentMonthRevenue = currentMonthOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const previousMonthRevenue = previousMonthOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const currentYearRevenue = currentYearOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const previousYearRevenue = previousYearOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    
    const growthMoM = previousMonthRevenue > 0 
      ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100 
      : null;
    const growthYoY = previousYearRevenue > 0 
      ? ((currentYearRevenue - previousYearRevenue) / previousYearRevenue) * 100 
      : null;
    
    // === ARPU ===
    const uniqueClients = new Set(operations.filter(op => op.client_id || op.userId).map(op => op.client_id || op.userId));
    const activeClients = uniqueClients.size;
    const totalRevenue = operations.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const arpu = activeClients > 0 ? totalRevenue / activeClients : 0;
    
    // === CONVERSION ===
    const quotesCreated = allOperations.filter(op => 
      ['en_attente_validation_client', 'en_negociation', 'devis_accepte', 'planifiee', 'terminee', 'livree', 'payee'].includes(op.status)
    ).length;
    const quotesAccepted = allOperations.filter(op => 
      ['devis_accepte', 'planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree', 'terminee', 'payee'].includes(op.status)
    ).length;
    const conversionRate = quotesCreated > 0 ? (quotesAccepted / quotesCreated) * 100 : 0;
    
    // === CANCELLATION ===
    const cancelledByClient = allOperations.filter(op => op.status === 'annulee_par_client').length;
    const cancelledByAdmin = allOperations.filter(op => op.status === 'annulee_admin').length;
    const totalCancelled = cancelledByClient + cancelledByAdmin;
    const cancellationRate = allOperations.length > 0 ? (totalCancelled / allOperations.length) * 100 : 0;
    
    // === ACQUISITION & RETENTION ===
    const clientsByMonth = {};
    allOperations.forEach(op => {
      const clientId = op.client_id || op.userId;
      if (!clientId) return;
      const monthKey = new Date(op.createdAt).toISOString().slice(0, 7);
      if (!clientsByMonth[clientId]) {
        clientsByMonth[clientId] = monthKey;
      } else if (monthKey < clientsByMonth[clientId]) {
        clientsByMonth[clientId] = monthKey;
      }
    });
    
    const currentMonthKey = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`;
    const prevMonthKey = currentMonth === 0 
      ? `${currentYear - 1}-12`
      : `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
    
    const newClientsThisMonth = Object.values(clientsByMonth).filter(m => m === currentMonthKey).length;
    const newClientsPrevMonth = Object.values(clientsByMonth).filter(m => m === prevMonthKey).length;
    
    // Retention: clients who had ops in both current and previous month
    const clientsThisMonth = new Set(currentMonthOps.map(op => op.client_id || op.userId).filter(Boolean));
    const clientsPrevMonth = new Set(previousMonthOps.map(op => op.client_id || op.userId).filter(Boolean));
    const returningClients = [...clientsThisMonth].filter(c => clientsPrevMonth.has(c)).length;
    const retentionRate = clientsPrevMonth.size > 0 ? (returningClients / clientsPrevMonth.size) * 100 : 0;
    
    // === PIPELINE ===
    const pipelineStatuses = ['demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client', 'en_negociation'];
    const pipelineOps = allOperations.filter(op => pipelineStatuses.includes(op.status));
    const pipelineValue = pipelineOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const pipelineCount = pipelineOps.length;
    const avgDealSize = pipelineCount > 0 ? pipelineValue / pipelineCount : 0;
    
    // === GEOGRAPHIC ===
    const intralandOps = operations.filter(op => 
      op.operationType === 'intraland' || op.intraInterLand === 'INTRALAND'
    );
    const interlandOps = operations.filter(op => 
      op.operationType === 'interland' || op.intraInterLand === 'INTERLAND'
    );
    const intralandRevenue = intralandOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    const interlandRevenue = interlandOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
    
    // === CONCENTRATION ===
    const revenueByClient = {};
    operations.forEach(op => {
      const clientId = op.client_id || op.userId;
      if (!clientId) return;
      if (!revenueByClient[clientId]) {
        revenueByClient[clientId] = { 
          revenue: 0, 
          ops: 0, 
          name: op.clientName || op.companyName || `Client ${clientId.slice(0, 8)}` 
        };
      }
      revenueByClient[clientId].revenue += op.estimatedPrice || 0;
      revenueByClient[clientId].ops++;
    });
    
    const sortedClients = Object.entries(revenueByClient)
      .sort((a, b) => b[1].revenue - a[1].revenue);
    const top10 = sortedClients.slice(0, 10);
    const top10Revenue = top10.reduce((sum, [, data]) => sum + data.revenue, 0);
    const top10Percent = totalRevenue > 0 ? (top10Revenue / totalRevenue) * 100 : 0;
    
    // === OPERATIONAL KPIs ===
    const totalDistanceKm = operations.reduce((sum, op) => sum + (parseFloat(op.distance_km) || 0), 0);
    const totalWeightTons = operations.reduce((sum, op) => sum + (parseFloat(op.weight) || 0), 0);
    const totalTrucks = operations.reduce((sum, op) => sum + (parseInt(op.numberOfTrucks) || 1), 0);
    const totalContainers = operations.reduce((sum, op) => sum + (parseInt(op.containerCount) || parseInt(op.numberOfContainers) || 0), 0);
    
    const opsWithDistance = operations.filter(op => parseFloat(op.distance_km) > 0).length;
    const avgDistancePerOp = opsWithDistance > 0 ? totalDistanceKm / opsWithDistance : 0;
    const avgWeightPerOp = operations.length > 0 ? totalWeightTons / operations.length : 0;
    
    // Incidents (check for incident field)
    const opsWithIncidents = operations.filter(op => op.hasIncident || op.incident || op.incidents?.length > 0);
    const totalIncidents = opsWithIncidents.length;
    const incidentRate = operations.length > 0 ? (totalIncidents / operations.length) * 100 : 0;
    
    // Vehicle type breakdown
    const vehicleTypeCounts = {};
    operations.forEach(op => {
      const vType = op.vehicleType || 'Non spécifié';
      vehicleTypeCounts[vType] = (vehicleTypeCounts[vType] || 0) + 1;
    });
    const vehicleTypeBreakdown = Object.entries(vehicleTypeCounts)
      .map(([type, count]) => ({ type, count, percent: (count / operations.length) * 100 }))
      .sort((a, b) => b.count - a.count);
    
    // Cargo type breakdown
    const cargoTypeCounts = {};
    operations.forEach(op => {
      const cargo = op.cargoType || op.cargoDescription?.slice(0, 30) || 'Non spécifié';
      cargoTypeCounts[cargo] = (cargoTypeCounts[cargo] || 0) + 1;
    });
    const cargoTypeBreakdown = Object.entries(cargoTypeCounts)
      .map(([type, count]) => ({ type, count, percent: (count / operations.length) * 100 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
    
    return {
      growthMoM,
      growthYoY,
      currentMonthRevenue,
      previousMonthRevenue,
      currentYearRevenue,
      previousYearRevenue,
      arpu,
      arpuChange: null,
      activeClients,
      conversionRate,
      quotesCreated,
      quotesAccepted,
      cancellationRate,
      cancelledByClient,
      cancelledByAdmin,
      newClientsThisMonth,
      newClientsPrevMonth,
      retentionRate,
      returningClients,
      totalActiveClients: activeClients,
      churnedClients: clientsPrevMonth.size - returningClients,
      pipelineValue,
      pipelineCount,
      avgDealSize,
      intralandCount: intralandOps.length,
      interlandCount: interlandOps.length,
      intralandRevenue,
      interlandRevenue,
      top10ClientsRevenue: top10Revenue,
      top10ClientsPercent: top10Percent,
      topClients: top10.map(([id, data]) => ({ id, ...data })),
      // Operational KPIs
      totalDistanceKm,
      totalWeightTons,
      totalTrucks,
      totalContainers,
      totalIncidents,
      incidentRate,
      avgDistancePerOp,
      avgWeightPerOp,
      vehicleTypeBreakdown,
      cargoTypeBreakdown
    };
  }, []);

  const fetchAllStats = useCallback(async (showLoader = true) => {
    if (showLoader) setRefreshing(true);
    
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      const { startDate, endDate } = getDateRange(activeFilters.period);
      
      // Fetch all data (including financial KPIs for consolidated revenue growth)
      const [usersRes, opsRes, advancedRes, financialRes] = await Promise.all([
        axios.get(`${API}/admin/users?pageSize=1&page=1`, { headers }),
        axios.get(`${API}/transport-requests?limit=100000`, { headers }),
        axios.get(`${API}/admin/dashboard/advanced-stats`, { headers }),
        canSeeStrategicKPIs
          ? axios.get(`${API}/admin/dashboard/financial-kpis?period=12m`, { headers }).catch(() => null)
          : Promise.resolve(null)
      ]);
      
      const totalUsers = usersRes.data.total || 0;
      const allOperations = Array.isArray(opsRes.data) ? opsRes.data : (opsRes.data.operations || opsRes.data.requests || []);
      
      // Filter operations by period and filters
      let operations = allOperations.filter(op => {
        const opDate = new Date(op.createdAt);
        const dateInRange = opDate >= startDate && opDate <= endDate;
        const sectorMatch = activeFilters.sector === 'all' || op.clientSector === activeFilters.sector;
        const typeMatch = activeFilters.operationType === 'all' || op.operationType === activeFilters.operationType;
        const clientMatch = selectedClient === 'all' || op.client_id === selectedClient || op.userId === selectedClient;
        return dateInRange && sectorMatch && typeMatch && clientMatch;
      });
      
      // Calculate status distribution
      const preparationStatuses = ['demande_enregistree', 'en_attente_de_devis', 'en_attente_validation_client', 'en_negociation', 'devis_accepte'];
      const executionStatuses = ['planifiee', 'prete_au_chargement', 'en_cours_de_chargement', 'charge_en_route', 'livree'];
      const facturationStatuses = ['en_attente_paiement', 'payee'];
      const annuleeStatuses = ['annulee_admin', 'annulee_par_client'];
      
      const preparation = operations.filter(op => preparationStatuses.includes(op.status)).length;
      const execution = operations.filter(op => executionStatuses.includes(op.status)).length;
      const facturation = operations.filter(op => facturationStatuses.includes(op.status)).length;
      const terminee = operations.filter(op => op.status === 'terminee').length;
      const annulee = operations.filter(op => annuleeStatuses.includes(op.status)).length;
      
      const revenuTotal = operations.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
      const completedOps = terminee + facturation;
      const completionRate = operations.length > 0 ? Math.round((completedOps / operations.length) * 100) : 0;
      const avgOperationValue = completedOps > 0 ? Math.round(revenuTotal / completedOps) : 0;
      
      setBasicStats({
        totalUsers,
        totalOperations: operations.length,
        preparation,
        execution,
        operationsEnCours: preparation + execution,
        operationsTerminees: completedOps,
        operationsAnnulees: annulee,
        revenuTotal,
        avgOperationValue,
        completionRate
      });
      
      setStatusData([
        { name: 'Préparation', value: preparation, color: '#3b82f6' },
        { name: 'Exécution', value: execution, color: '#1db584' },
        { name: 'Facturation', value: facturation, color: '#f59e0b' },
        { name: 'Terminée', value: terminee, color: '#22c55e' },
        { name: 'Annulée', value: annulee, color: '#ef4444' }
      ]);
      
      // Generate trend data
      const trendMap = new Map();
      operations.forEach(op => {
        const date = new Date(op.createdAt);
        let key;
        if (activeFilters.period === '7d') {
          key = date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' });
        } else if (activeFilters.period === '30d' || activeFilters.period === '90d') {
          key = date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
        } else {
          key = date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
        }
        
        if (!trendMap.has(key)) {
          trendMap.set(key, { period: key, operations: 0, revenue: 0 });
        }
        const entry = trendMap.get(key);
        entry.operations++;
        entry.revenue += op.estimatedPrice || 0;
      });
      
      setTrendData(Array.from(trendMap.values()).slice(-12));
      setAdvancedStats(advancedRes.data);
      
      // Calculate strategic KPIs (only if user has access)
      if (canSeeStrategicKPIs) {
        const kpis = calculateStrategicKPIs(operations, allOperations, []);

        // ── Override growth rates avec données financières consolidées (spot + contrats) ──
        if (financialRes?.data?.revenueEvolution?.data) {
          const monthlyData = financialRes.data.revenueEvolution.data;
          // Les données sont triées du plus ancien au plus récent
          const currentMonthData = monthlyData[monthlyData.length - 1];
          const prevMonthData = monthlyData[monthlyData.length - 2];

          if (currentMonthData && prevMonthData && prevMonthData.revenue > 0) {
            kpis.growthMoM = ((currentMonthData.revenue - prevMonthData.revenue) / prevMonthData.revenue) * 100;
            kpis.previousMonthRevenue = prevMonthData.revenue;
          }
          // Mise à jour des revenus courants avec données consolidées
          if (currentMonthData) {
            kpis.currentMonthRevenue = currentMonthData.revenue;
          }
          // YoY : total des 12 mois vs total des 12 mois précédents
          const totalCurrentYear = financialRes.data.totalRevenue || 0;
          kpis.currentYearRevenue = totalCurrentYear;
          // growthYoY reste N/A (pas de données N-1 disponibles) — on affiche le YTD
        }

        setStrategicKPIs(kpis);

        // Fetch objectives progress for overview tab
        try {
          const objRes = await axios.get(`${API}/admin/objectives/progress`, { headers });
          setObjectivesProgress(objRes.data);
        } catch (e) {
          console.warn('Could not fetch objectives progress:', e);
        }

        // Monthly growth data pour le graphique — utiliser les données consolidées si disponibles
        if (financialRes?.data?.revenueEvolution?.data) {
          const evol = financialRes.data.revenueEvolution.data;
          // Agrandir à 6 points max
          const last6 = evol.slice(-6);
          setMonthlyGrowthData(last6.map(d => ({
            month: d.label,
            revenue: d.revenue,
            spotRevenue: d.spotRevenue || 0,
            contractRevenue: d.contractRevenue || 0,
            operations: d.operations
          })));
        } else {
          // Fallback : données uniquement spot depuis allOperations
          const monthlyFallback = [];
          for (let i = 5; i >= 0; i--) {
            const d = new Date();
            d.setMonth(d.getMonth() - i);
            const monthKey = d.toISOString().slice(0, 7);
            const monthOps = allOperations.filter(op => op.createdAt?.startsWith(monthKey));
            const monthRevenue = monthOps.reduce((sum, op) => sum + (op.estimatedPrice || 0), 0);
            monthlyFallback.push({
              month: d.toLocaleDateString('fr-FR', { month: 'short' }),
              revenue: monthRevenue,
              operations: monthOps.length
            });
          }
          setMonthlyGrowthData(monthlyFallback);
        }
      }
      
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les statistiques', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeFilters, selectedClient, getDateRange, toast, canSeeStrategicKPIs, calculateStrategicKPIs]);

  useEffect(() => {
    fetchAllStats();
  }, [fetchAllStats]);

  // Load clients list for commercial filter
  useEffect(() => {
    if (!isCommercial) return;
    // Load all client pages (max 100 per page)
    const loadClients = async () => {
      try {
        const res = await axios.get(`${API}/admin/users?pageSize=100&page=1&role=client`, {});
        const data = res.data;
        const users = data.users || [];
        // If there are more pages, load page 2 as well (max 200 clients)
        if (data.totalPages > 1) {
          const res2 = await axios.get(`${API}/admin/users?pageSize=100&page=2&role=client`, {});
          const users2 = res2.data.users || [];
          setClients([...users, ...users2]);
        } else {
          setClients(users);
        }
      } catch (e) {}
    };
    loadClients();
  }, [isCommercial]);

  const applyFilters = () => {
    setActiveFilters({ ...filters });
    setShowFilters(false);
  };

  const resetFilters = () => {
    const defaultFilters = { period: '30d', sector: 'all', operationType: 'all' };
    setFilters(defaultFilters);
    setActiveFilters(defaultFilters);
    setShowFilters(false);
  };

  const hasActiveFilters = activeFilters.sector !== 'all' || activeFilters.operationType !== 'all' || activeFilters.period !== '30d';

  // Export handlers
  const handleExportCSV = (type) => {
    setShowExportMenu(false);
    
    switch (type) {
      case 'kpi':
        const kpiData = [{
          'Utilisateurs totaux': basicStats.totalUsers,
          'Opérations': basicStats.totalOperations,
          'CA Total (XOF)': basicStats.revenuTotal,
          'Taux de complétion (%)': basicStats.completionRate,
        }];
        if (canSeeStrategicKPIs) {
          Object.assign(kpiData[0], {
            'Croissance MoM (%)': strategicKPIs.growthMoM?.toFixed(1) || 'N/A',
            'Croissance YoY (%)': strategicKPIs.growthYoY?.toFixed(1) || 'N/A',
            'ARPU (XOF)': strategicKPIs.arpu,
            'Taux conversion (%)': strategicKPIs.conversionRate.toFixed(1),
            'Taux annulation (%)': strategicKPIs.cancellationRate.toFixed(1),
            'Nouveaux clients': strategicKPIs.newClientsThisMonth,
            'Taux rétention (%)': strategicKPIs.retentionRate.toFixed(1),
            'Pipeline (XOF)': strategicKPIs.pipelineValue,
            'Concentration Top 10 (%)': strategicKPIs.top10ClientsPercent.toFixed(1)
          });
        }
        exportToCSV(kpiData, 'dashboard_kpi');
        break;
      case 'sectors':
        exportToCSV(advancedStats.bySector.map(s => ({
          'Secteur': s.sectorLabel,
          'Opérations': s.operations,
          'CA (XOF)': s.revenue,
          'Clients': s.clients || 0
        })), 'dashboard_secteurs');
        break;
      case 'companies':
        exportToCSV(advancedStats.topCompanies.map((c, i) => ({
          'Rang': i + 1,
          'Entreprise': c.companyName,
          'Secteur': c.sectorLabel,
          'Opérations': c.operations,
          'CA (XOF)': c.revenue
        })), 'dashboard_top_entreprises');
        break;
      case 'trends':
        exportToCSV(trendData.map(t => ({
          'Période': t.period,
          'Opérations': t.operations,
          'CA (XOF)': t.revenue
        })), 'dashboard_tendances');
        break;
      default:
        break;
    }
    
    toast({ title: '✅ Export réussi', description: 'Le fichier CSV a été téléchargé' });
  };

  const handleExportPDF = async () => {
    setShowExportMenu(false);
    try {
      await exportToPDF('dashboard-content', 'Tableau de Bord - Baobab Connect', setExportingPDF);
      toast({ title: '✅ Export PDF', description: 'Le fichier PDF a été téléchargé avec succès' });
    } catch (error) {
      toast({ title: '❌ Erreur', description: 'Impossible de générer le PDF', variant: 'destructive' });
    }
  };

  // KPI Card Component
  const KPICard = ({ title, value, subValue, icon: Icon, trend, trendValue, color = 'blue', size = 'normal', progress, statusLabel, onClick, testId }) => {
    const isObjective = progress !== undefined;
    const isClickable = typeof onClick === 'function';

    const colorClasses = {
      blue: 'from-blue-50 to-blue-100 border-blue-200',
      green: 'from-green-50 to-green-100 border-green-200',
      emerald: 'from-emerald-50 to-emerald-100 border-emerald-200',
      purple: 'from-purple-50 to-purple-100 border-purple-200',
      amber: 'from-amber-50 to-amber-100 border-amber-200',
      red: 'from-red-50 to-red-100 border-red-200',
      cyan: 'from-cyan-50 to-cyan-100 border-cyan-200',
      indigo: 'from-indigo-50 to-indigo-100 border-indigo-200',
      pink: 'from-pink-50 to-pink-100 border-pink-200',
      orange: 'from-orange-50 to-orange-100 border-orange-200'
    };
    
    const iconColors = {
      blue: 'text-blue-500',
      green: 'text-green-500',
      emerald: 'text-emerald-500',
      purple: 'text-purple-500',
      amber: 'text-amber-500',
      red: 'text-red-500',
      cyan: 'text-cyan-500',
      indigo: 'text-indigo-500',
      pink: 'text-pink-500',
      orange: 'text-orange-500'
    };
    
    const textColors = {
      blue: 'text-blue-700',
      green: 'text-green-700',
      emerald: 'text-emerald-700',
      purple: 'text-purple-700',
      amber: 'text-amber-700',
      red: 'text-red-700',
      cyan: 'text-cyan-700',
      indigo: 'text-indigo-700',
      pink: 'text-pink-700',
      orange: 'text-orange-700'
    };

    // Status badge styles for objective cards
    const statusStyles = {
      'Atteint': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'En bonne voie': 'bg-amber-100 text-amber-700 border-amber-200',
      'En cours': 'bg-blue-100 text-blue-700 border-blue-200',
      'Non démarré': 'bg-slate-100 text-slate-500 border-slate-200',
      'Attention': 'bg-red-100 text-red-700 border-red-200',
    };

    // Progress bar color
    const progressBarColor = progress >= 80 ? 'bg-emerald-500' : progress >= 50 ? 'bg-amber-400' : progress > 0 ? 'bg-blue-400' : 'bg-slate-300';
    
    return (
      <Card
        className={`${isObjective ? 'bg-white border border-slate-200 shadow-sm overflow-hidden' : `bg-gradient-to-br ${colorClasses[color]}`} ${isClickable ? 'cursor-pointer hover:shadow-md hover:scale-[1.015] transition-all' : ''}`}
        onClick={onClick}
        data-testid={testId}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      >
        <CardContent className={`${size === 'large' ? 'p-4' : 'p-3'} ${isObjective ? 'pb-2' : ''}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <p className={`text-xs font-medium ${isObjective ? 'text-slate-500' : textColors[color].replace('700', '600')}`}>{title}</p>
                {statusLabel && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${statusStyles[statusLabel] || statusStyles['En cours']}`}>
                    {statusLabel}
                  </span>
                )}
              </div>
              <p
                className={`${size === 'large' ? 'text-2xl' : 'text-xl'} font-bold ${isObjective ? 'text-slate-900' : textColors[color]}`}
                title={value === 'Nouveau' ? 'Pas de données pour la période précédente — comparaison impossible' : undefined}
              >
                {value}
              </p>
              {value === 'Nouveau' && (
                <p className="text-[10px] text-gray-400 mt-0.5 italic">Pas de données période précédente</p>
              )}
              {subValue && value !== 'Nouveau' && <p className="text-xs text-gray-500 mt-0.5">{subValue}</p>}
            </div>
            <div className="flex flex-col items-end">
              {isObjective ? (
                <div className={`p-2 rounded-lg ${progress >= 80 ? 'bg-emerald-50' : progress >= 50 ? 'bg-amber-50' : 'bg-blue-50'}`}>
                  <Icon className={`${size === 'large' ? 'h-5 w-5' : 'h-4 w-4'} ${progress >= 80 ? 'text-emerald-500' : progress >= 50 ? 'text-amber-500' : 'text-blue-500'}`} />
                </div>
              ) : (
                <Icon className={`${size === 'large' ? 'h-7 w-7' : 'h-6 w-6'} ${iconColors[color]}`} />
              )}
              {trend !== undefined && trendValue !== null && (
                <div className={`flex items-center mt-1 text-xs font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                  {formatGrowth(trendValue)}
                </div>
              )}
            </div>
          </div>
        </CardContent>
        {isObjective && (
          <div className="px-3 pb-3">
            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full ${progressBarColor} transition-all duration-700`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-[#1db584]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 relative">
      {/* PDF Export Loading Overlay */}
      {exportingPDF && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-2xl flex flex-col items-center gap-4">
            <Loader2 className="h-10 w-10 animate-spin text-[#1db584]" />
            <div className="text-center">
              <p className="font-semibold text-gray-900">Génération du PDF en cours...</p>
              <p className="text-sm text-gray-500 mt-1">Veuillez patienter, cela peut prendre quelques secondes</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg md:text-2xl font-bold text-gray-800">Tableau de Bord</h1>
          <p className="text-sm text-gray-500 mt-1">
            {PERIOD_OPTIONS.find(p => p.value === activeFilters.period)?.label}
            {activeFilters.sector !== 'all' && ` • ${SECTOR_LABELS[activeFilters.sector]}`}
            {activeFilters.operationType !== 'all' && ` • ${OPERATION_TYPE_OPTIONS.find(t => t.value === activeFilters.operationType)?.label}`}
            {isCommercial && selectedClient !== 'all' && ` • ${clients.find(c => c.id === selectedClient)?.companyName || clients.find(c => c.id === selectedClient)?.firstName || 'Client sélectionné'}`}
          </p>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          {/* Client filter for commercial role */}
          {isCommercial && (
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger className="w-44 h-8 text-xs bg-white border-[#1db584]/40">
                <User className="h-3 w-3 mr-1 text-[#1db584]" />
                <SelectValue placeholder="Tous les clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName || `${c.firstName} ${c.lastName}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Real-time connection indicator */}
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${wsConnected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`} title={wsConnected ? 'Notifications temps réel actives' : 'Reconnexion en cours...'}>
            {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Déconnecté'}</span>
          </div>
          
          <Button variant="outline" size="sm" onClick={() => fetchAllStats(true)} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>

          {/* Sélecteur de période — toujours visible pour éviter toute confusion sur la fenêtre de données */}
          <Select
            value={activeFilters.period}
            onValueChange={(v) => {
              setFilters(f => ({ ...f, period: v }));
              setActiveFilters(f => ({ ...f, period: v }));
            }}
          >
            <SelectTrigger
              className="h-8 w-40 text-xs bg-white border-[#1db584]/40"
              data-testid="dashboard-period-selector"
            >
              <Calendar className="h-3 w-3 mr-1 text-[#1db584]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`period-option-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant={hasActiveFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={hasActiveFilters ? "bg-[#1db584] hover:bg-[#19a074]" : ""}
          >
            <Filter className="h-4 w-4 mr-1" />
            Filtres
          </Button>
          
          <div className="relative" ref={exportMenuRef}>
            <Button variant="outline" size="sm" onClick={() => setShowExportMenu(!showExportMenu)}>
              <Download className="h-4 w-4 mr-1" />
              Exporter
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border z-50">
                <div className="p-2">
                  <p className="text-xs font-medium text-gray-500 px-2 py-1">Export CSV</p>
                  <button onClick={() => handleExportCSV('kpi')} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    KPIs {canSeeStrategicKPIs && 'stratégiques'}
                  </button>
                  <button onClick={() => handleExportCSV('sectors')} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    Données par secteur
                  </button>
                  <button onClick={() => handleExportCSV('companies')} className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    Top entreprises
                  </button>
                  <div className="border-t my-2"></div>
                  <button 
                    onClick={handleExportPDF} 
                    disabled={exportingPDF}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {exportingPDF ? (
                      <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 text-red-600" />
                    )}
                    {exportingPDF ? 'Génération en cours...' : 'Rapport PDF'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <Card className="border-[#1db584]/30 bg-[#1db584]/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Période</label>
                <Select value={filters.period} onValueChange={(v) => setFilters(f => ({ ...f, period: v }))}>
                  <SelectTrigger className="bg-white">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIOD_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Secteur</label>
                <Select value={filters.sector} onValueChange={(v) => setFilters(f => ({ ...f, sector: v }))}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {Object.entries(SECTOR_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1 min-w-[150px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Type d'opération</label>
                <Select value={filters.operationType} onValueChange={(v) => setFilters(f => ({ ...f, operationType: v }))}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OPERATION_TYPE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2">
                <Button size="sm" onClick={applyFilters} className="bg-[#1db584] hover:bg-[#19a074]">Appliquer</Button>
                <Button size="sm" variant="outline" onClick={resetFilters}><X className="h-4 w-4 mr-1" />Réinitialiser</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <div id="dashboard-content">
        {/* Slim Billing Alert Banner - between header and tabs */}
        <BillingAlertBanner onNavigateToRecouvrement={() => setActiveTab('recouvrement')} />
        <WorkflowDivergenceCounter onClick={() => {
          window.location.href = '/admin/analytics/divergences';
        }} />

        {/* Tabs Navigation for KPIs - visible to all staff */}
        <Tabs value={activeTab || (canSeeStrategicKPIs ? "overview" : "operational")} onValueChange={setActiveTab} className="mb-6">
            <TabsList className="bg-gray-100/80 p-1 rounded-lg">
              {canSeeStrategicKPIs && (
                <TabsTrigger value="overview" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600">
                  <Crown className="h-4 w-4 mr-2" />
                  Vue d'ensemble
                </TabsTrigger>
              )}
              <TabsTrigger value="operational" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600">
                <Activity className="h-4 w-4 mr-2" />
                KPIs Opérationnels
              </TabsTrigger>
              {canSeeFinancialTabs && (
                <TabsTrigger value="financial" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600">
                  <DollarSign className="h-4 w-4 mr-2" />
                  KPIs Financiers
                </TabsTrigger>
              )}
              {canSeeFinancialTabs && (
                <TabsTrigger value="recouvrement" className="data-[state=active]:bg-white data-[state=active]:text-red-600" data-testid="recouvrement-tab">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Recouvrement
                </TabsTrigger>
              )}
              <TabsTrigger value="objectives" className="data-[state=active]:bg-white data-[state=active]:text-violet-600" data-testid="objectives-tab">
                <Target className="h-4 w-4 mr-2" />
                Objectifs
              </TabsTrigger>
              {canSeeFinancialTabs && (
                <TabsTrigger value="profitability" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600" data-testid="profitability-tab">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Rentabilité
                </TabsTrigger>
              )}
              {canSeeStrategicKPIs && (
              <TabsTrigger value="fleet" className="data-[state=active]:bg-white data-[state=active]:text-emerald-600" data-testid="fleet-tab">
                <Truck className="h-4 w-4 mr-2" />
                Flotte
              </TabsTrigger>
              )}
            </TabsList>
            
            {/* Tab: Vue d'ensemble (strategic KPIs - admin/direction only) */}
            {canSeeStrategicKPIs && (
            <TabsContent value="overview" className="mt-6">
              {/* Pipeline Opérationnel — KPIs cliquables (drill-down vers /admin/operations?status=...) */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="h-5 w-5 text-emerald-500" />
                  <h2 className="text-lg font-semibold text-gray-800">Pipeline Opérationnel</h2>
                  <Badge variant="outline" className="text-xs">Cliquez pour filtrer</Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  <KPICard
                    title="Opérations totales"
                    value={basicStats.totalOperations}
                    subValue="Toutes confondues"
                    icon={Truck}
                    color="emerald"
                    size="large"
                    onClick={() => navigate('/admin/operations')}
                    testId="kpi-admin-ops-total"
                  />
                  <KPICard
                    title="En préparation"
                    value={basicStats.preparation}
                    subValue="Devis, validation, négo"
                    icon={ClipboardList}
                    color="blue"
                    size="large"
                    onClick={() => navigate('/admin/operations?status=preparation')}
                    testId="kpi-admin-ops-preparation"
                  />
                  <KPICard
                    title="En exécution"
                    value={basicStats.execution}
                    subValue="Chargement, transit, livraison"
                    icon={Clock}
                    color="amber"
                    size="large"
                    onClick={() => navigate('/admin/operations?status=execution')}
                    testId="kpi-admin-ops-execution"
                  />
                  <KPICard
                    title="Terminées"
                    value={basicStats.operationsTerminees}
                    subValue={`${basicStats.completionRate}% complétion`}
                    icon={CheckCircle}
                    color="green"
                    size="large"
                    onClick={() => navigate('/admin/operations?status=terminee')}
                    testId="kpi-admin-ops-terminees"
                  />
                  <KPICard
                    title="Annulées"
                    value={basicStats.operationsAnnulees}
                    subValue="Client + admin"
                    icon={XCircle}
                    color={basicStats.operationsAnnulees > 0 ? 'red' : 'cyan'}
                    size="large"
                    onClick={() => navigate('/admin/operations?status=annulee_par_client')}
                    testId="kpi-admin-ops-annulees"
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <h2 className="text-lg font-semibold text-gray-800">KPIs Stratégiques</h2>
                  <Badge variant="outline" className="text-xs">Direction</Badge>
                </div>
              
              {/* Growth & Financial KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
                <KPICard
                  title="Croissance MoM"
                  value={formatGrowth(strategicKPIs.growthMoM)}
                  subValue={`${formatExact(strategicKPIs.currentMonthRevenue)} FCFA ce mois`}
                  icon={TrendingUp}
                  color={strategicKPIs.growthMoM === null ? 'blue' : strategicKPIs.growthMoM >= 0 ? 'green' : 'red'}
                  size="large"
                />
                <KPICard
                  title="Croissance YoY"
                  value={formatGrowth(strategicKPIs.growthYoY)}
                  subValue={`${formatExact(strategicKPIs.currentYearRevenue)} FCFA YTD`}
                  icon={TrendingUp}
                  color={strategicKPIs.growthYoY === null ? 'blue' : strategicKPIs.growthYoY >= 0 ? 'emerald' : 'red'}
                  size="large"
                />
                <KPICard
                  title="ARPU"
                  value={`${formatExact(strategicKPIs.arpu)} FCFA`}
                  subValue={`${strategicKPIs.activeClients} clients actifs`}
                  icon={DollarSign}
                  color="purple"
                  size="large"
                />
                <KPICard
                  title="Pipeline"
                  value={`${formatExact(strategicKPIs.pipelineValue)} FCFA`}
                  subValue={`${strategicKPIs.pipelineCount} devis en attente`}
                  icon={Wallet}
                  color="cyan"
                  size="large"
                />
                <KPICard
                  title="Concentration Top 10"
                  value={`${strategicKPIs.top10ClientsPercent.toFixed(0)}%`}
                  subValue={`${formatExact(strategicKPIs.top10ClientsRevenue)} FCFA du CA`}
                  icon={AlertTriangle}
                  color={strategicKPIs.top10ClientsPercent > 50 ? 'red' : 'amber'}
                  size="large"
                />
              </div>
              
              {/* Conversion & Retention KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
                <KPICard
                  title="Taux Conversion"
                  value={`${strategicKPIs.conversionRate.toFixed(0)}%`}
                  subValue={`${strategicKPIs.quotesAccepted}/${strategicKPIs.quotesCreated} devis`}
                  icon={Target}
                  color="green"
                />
                <KPICard
                  title="Taux Annulation"
                  value={`${strategicKPIs.cancellationRate.toFixed(1)}%`}
                  subValue={`${strategicKPIs.cancelledByClient + strategicKPIs.cancelledByAdmin} annulées`}
                  icon={XCircle}
                  color={strategicKPIs.cancellationRate > 10 ? 'red' : 'amber'}
                />
                <KPICard
                  title="Nouveaux Clients"
                  value={strategicKPIs.newClientsThisMonth}
                  subValue={`vs ${strategicKPIs.newClientsPrevMonth} mois préc.`}
                  icon={UserPlus}
                  trend={strategicKPIs.newClientsThisMonth - strategicKPIs.newClientsPrevMonth}
                  trendValue={strategicKPIs.newClientsPrevMonth > 0 ? ((strategicKPIs.newClientsThisMonth - strategicKPIs.newClientsPrevMonth) / strategicKPIs.newClientsPrevMonth) * 100 : null}
                  color="blue"
                />
                <KPICard
                  title="Taux Rétention"
                  value={`${strategicKPIs.retentionRate.toFixed(0)}%`}
                  subValue={`${strategicKPIs.returningClients} récurrents`}
                  icon={Repeat}
                  color={strategicKPIs.retentionRate >= 70 ? 'green' : strategicKPIs.retentionRate >= 50 ? 'amber' : 'red'}
                />
                <KPICard
                  title="Intraland"
                  value={strategicKPIs.intralandCount}
                  subValue={`${formatExact(strategicKPIs.intralandRevenue)} FCFA`}
                  icon={MapPin}
                  color="indigo"
                />
                <KPICard
                  title="Interland"
                  value={strategicKPIs.interlandCount}
                  subValue={`${formatExact(strategicKPIs.interlandRevenue)} FCFA`}
                  icon={MapPin}
                  color="orange"
                />
              </div>
            </div>

            {/* Objectives KPIs Section */}
            {objectivesProgress && (() => {
              const objStatus = (pct) => {
                if (!pct || pct <= 0) return 'Non démarré';
                if (pct < 50) return 'En cours';
                if (pct < 80) return 'En bonne voie';
                return 'Atteint';
              };
              const dsoStatus = (dso) => dso?.on_target ? 'Atteint' : dso?.actual === 0 ? 'Non démarré' : 'Attention';

              return (
            <div className="mb-6" data-testid="overview-objectives-section">
              <div className="flex items-center gap-2 mb-3">
                <Target className="h-5 w-5 text-violet-500" />
                <h2 className="text-lg font-semibold text-gray-800">Objectifs Business</h2>
                <Badge variant="outline" className="text-xs">vs Cibles</Badge>
              </div>

              {/* Financial objectives */}
              {(objectivesProgress.revenue?.monthly?.target > 0 || objectivesProgress.revenue?.quarterly?.target > 0 || objectivesProgress.revenue?.yearly?.target > 0) && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
                {objectivesProgress.revenue?.monthly?.target > 0 && (
                  <KPICard
                    title="Obj. CA Mensuel"
                    value={`${Math.round(objectivesProgress.revenue.monthly.percent || 0)}%`}
                    subValue={`${formatExact(objectivesProgress.revenue.monthly.actual)} / ${formatExact(objectivesProgress.revenue.monthly.target)} FCFA`}
                    icon={DollarSign}
                    size="large"
                    progress={objectivesProgress.revenue.monthly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.revenue.monthly.percent)}
                  />
                )}
                {objectivesProgress.revenue?.quarterly?.target > 0 && (
                  <KPICard
                    title="Obj. CA Trim."
                    value={`${Math.round(objectivesProgress.revenue.quarterly.percent || 0)}%`}
                    subValue={`${formatExact(objectivesProgress.revenue.quarterly.actual)} / ${formatExact(objectivesProgress.revenue.quarterly.target)} FCFA`}
                    icon={BarChart3}
                    size="large"
                    progress={objectivesProgress.revenue.quarterly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.revenue.quarterly.percent)}
                  />
                )}
                {objectivesProgress.revenue?.yearly?.target > 0 && (
                  <KPICard
                    title="Obj. CA Annuel"
                    value={`${Math.round(objectivesProgress.revenue.yearly.percent || 0)}%`}
                    subValue={`${formatExact(objectivesProgress.revenue.yearly.actual)} / ${formatExact(objectivesProgress.revenue.yearly.target)} FCFA`}
                    icon={TrendingUp}
                    size="large"
                    progress={objectivesProgress.revenue.yearly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.revenue.yearly.percent)}
                  />
                )}
                {objectivesProgress.arr && (
                  <KPICard
                    title="MRR (Mensuel)"
                    value={`${formatExact(objectivesProgress.arr.totalMonthly)} FCFA`}
                    subValue={`${objectivesProgress.arr.contracts?.length || 0} contrats · ${formatExact(objectivesProgress.arr.totalAnnual)}/an`}
                    icon={Wallet}
                    color="emerald"
                    size="large"
                  />
                )}
                {strategicKPIs.pipelineValue > 0 && (
                  <KPICard
                    title="Pipeline"
                    value={`${formatExact(strategicKPIs.pipelineValue)} FCFA`}
                    subValue={`${strategicKPIs.pipelineCount} devis en attente`}
                    icon={TrendingUp}
                    color="cyan"
                    size="large"
                  />
                )}
              </div>
              )}

              {/* Operational objectives */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {objectivesProgress.operations?.monthly?.target > 0 && (
                  <KPICard
                    title="Obj. Ops/Mois"
                    value={`${Math.round(objectivesProgress.operations.monthly.percent || 0)}%`}
                    subValue={`${objectivesProgress.operations.monthly.actual} / ${objectivesProgress.operations.monthly.target} ops`}
                    icon={Truck}
                    progress={objectivesProgress.operations.monthly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.operations.monthly.percent)}
                  />
                )}
                {objectivesProgress.operations?.weekly?.target > 0 && (
                  <KPICard
                    title="Obj. Ops/Sem."
                    value={`${Math.round(objectivesProgress.operations.weekly.percent || 0)}%`}
                    subValue={`${objectivesProgress.operations.weekly.actual} / ${objectivesProgress.operations.weekly.target} ops`}
                    icon={Truck}
                    progress={objectivesProgress.operations.weekly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.operations.weekly.percent)}
                  />
                )}
                {objectivesProgress.collection?.rate?.target > 0 && (
                  <KPICard
                    title="Obj. Recouvrement"
                    value={`${objectivesProgress.collection.rate.actual}%`}
                    subValue={`Cible: ${objectivesProgress.collection.rate.target}%`}
                    icon={CheckCircle}
                    progress={objectivesProgress.collection.rate.percent || 0}
                    statusLabel={objStatus(objectivesProgress.collection.rate.percent)}
                  />
                )}
                {objectivesProgress.collection?.dso && (
                  <KPICard
                    title="DSO"
                    value={`${objectivesProgress.collection.dso.actual}j`}
                    subValue={`Cible: ${objectivesProgress.collection.dso.target}j`}
                    icon={Clock}
                    progress={objectivesProgress.collection.dso.on_target ? 100 : Math.max(0, 100 - Math.abs(objectivesProgress.collection.dso.actual - objectivesProgress.collection.dso.target) * 5)}
                    statusLabel={dsoStatus(objectivesProgress.collection.dso)}
                  />
                )}
                {objectivesProgress.new_clients?.monthly?.target > 0 && (
                  <KPICard
                    title="Nvx Clients/Mois"
                    value={`${objectivesProgress.new_clients.monthly.actual}`}
                    subValue={`Cible: ${objectivesProgress.new_clients.monthly.target}`}
                    icon={UserPlus}
                    progress={objectivesProgress.new_clients.monthly.percent || 0}
                    statusLabel={objStatus(objectivesProgress.new_clients.monthly.percent)}
                  />
                )}
              </div>
            </div>
              );
            })()
            }
            
            {/* Monthly Growth Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* CA Comparison MoM/YoY */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[#1db584]" />
                    Comparatif CA
                  </CardTitle>
                  <CardDescription>Mois et Année précédents</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart 
                      data={[
                        { 
                          name: 'Mensuel', 
                          'Actuel': strategicKPIs.currentMonthRevenue, 
                          'Précédent': strategicKPIs.previousMonthRevenue 
                        },
                        { 
                          name: 'Annuel', 
                          'Actuel': strategicKPIs.currentYearRevenue, 
                          'Précédent': strategicKPIs.previousYearRevenue 
                        }
                      ]}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={formatAmount} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={60} />
                      <Tooltip formatter={(value) => `${formatExact(value)} XOF`} />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Bar dataKey="Précédent" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="Actuel" fill="#1db584" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Funnel de Conversion */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <GitBranch className="h-5 w-5 text-[#1db584]" />
                    Funnel Conversion
                  </CardTitle>
                  <CardDescription>Pipeline commercial</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[
                      { label: 'Demandes', value: basicStats.totalOperations, color: '#3b82f6', percent: 100 },
                      { label: 'Devis créés', value: strategicKPIs.quotesCreated, color: '#8b5cf6', percent: basicStats.totalOperations > 0 ? (strategicKPIs.quotesCreated / basicStats.totalOperations) * 100 : 0 },
                      { label: 'Devis acceptés', value: strategicKPIs.quotesAccepted, color: '#1db584', percent: basicStats.totalOperations > 0 ? (strategicKPIs.quotesAccepted / basicStats.totalOperations) * 100 : 0 },
                      { label: 'Terminées', value: basicStats.operationsTerminees, color: '#22c55e', percent: basicStats.totalOperations > 0 ? (basicStats.operationsTerminees / basicStats.totalOperations) * 100 : 0 }
                    ].map((item, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium text-gray-600">{item.label}</span>
                          <span className="text-xs font-bold" style={{ color: item.color }}>{item.value}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div 
                            className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                            style={{ 
                              width: `${Math.max(item.percent, 5)}%`, 
                              backgroundColor: item.color 
                            }}
                          >
                            <span className="text-[10px] font-bold text-white">
                              {item.percent.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Gauge Rétention */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Repeat className="h-5 w-5 text-[#1db584]" />
                    Taux de Rétention
                  </CardTitle>
                  <CardDescription>Clients récurrents</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <ResponsiveContainer width="100%" height={140}>
                    <RadialBarChart 
                      cx="50%" 
                      cy="100%" 
                      innerRadius="60%" 
                      outerRadius="100%" 
                      startAngle={180} 
                      endAngle={0}
                      data={[
                        { name: 'Rétention', value: strategicKPIs.retentionRate, fill: strategicKPIs.retentionRate >= 70 ? '#22c55e' : strategicKPIs.retentionRate >= 50 ? '#f59e0b' : '#ef4444' }
                      ]}
                    >
                      <RadialBar 
                        background={{ fill: '#e5e7eb' }}
                        dataKey="value" 
                        cornerRadius={10}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                  <div className="text-center -mt-8">
                    <p className="text-xl md:text-3xl font-bold text-gray-800">{strategicKPIs.retentionRate.toFixed(0)}%</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {strategicKPIs.returningClients} récurrents sur {strategicKPIs.returningClients + strategicKPIs.churnedClients}
                    </p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        strategicKPIs.retentionRate >= 70 ? 'bg-green-100 text-green-700' :
                        strategicKPIs.retentionRate >= 50 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {strategicKPIs.retentionRate >= 70 ? '✓ Excellent' : 
                         strategicKPIs.retentionRate >= 50 ? '⚠ À améliorer' : '✗ Critique'}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 2: More Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Monthly Evolution */}
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-[#1db584]" />
                    Évolution mensuelle du CA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={monthlyGrowthData}>
                      <defs>
                        <linearGradient id="colorCA" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1db584" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#1db584" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tickFormatter={formatAmount} tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value, name) => [name === 'CA' ? `${formatExact(value)} XOF` : value, name]} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <Area yAxisId="left" type="monotone" dataKey="revenue" name="CA" stroke="#1db584" fill="url(#colorCA)" />
                      <Line yAxisId="right" type="monotone" dataKey="operations" name="Opérations" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Concentration Risque */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Concentration CA
                  </CardTitle>
                  <CardDescription>Risque de dépendance</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Top 10 clients', value: strategicKPIs.top10ClientsRevenue, fill: strategicKPIs.top10ClientsPercent > 50 ? '#ef4444' : '#f59e0b' },
                          { name: 'Autres clients', value: basicStats.revenuTotal - strategicKPIs.top10ClientsRevenue, fill: '#22c55e' }
                        ].filter(d => d.value > 0)}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={65}
                        paddingAngle={2}
                        dataKey="value"
                      >
                      </Pie>
                      <Tooltip formatter={(value) => `${formatExact(value)} XOF`} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="text-center -mt-2">
                    <p className="text-2xl font-bold text-gray-800">{strategicKPIs.top10ClientsPercent.toFixed(0)}%</p>
                    <p className="text-xs text-gray-500">du CA par le Top 10</p>
                    <div className={`mt-2 px-3 py-1 rounded-full text-xs font-medium inline-block ${
                      strategicKPIs.top10ClientsPercent > 70 ? 'bg-red-100 text-red-700' :
                      strategicKPIs.top10ClientsPercent > 50 ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {strategicKPIs.top10ClientsPercent > 70 ? '⚠️ Risque élevé' :
                       strategicKPIs.top10ClientsPercent > 50 ? '⚠ Risque modéré' :
                       '✓ Diversifié'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 3: Acquisition & Geographic */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Client Acquisition Trend */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-blue-500" />
                    Acquisition Clients
                  </CardTitle>
                  <CardDescription>Nouveaux vs Récurrents par mois</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-700">{strategicKPIs.newClientsThisMonth}</p>
                      <p className="text-xs text-blue-600">Nouveaux ce mois</p>
                      <p className={`text-xs mt-1 font-medium ${
                        strategicKPIs.newClientsThisMonth >= strategicKPIs.newClientsPrevMonth ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {strategicKPIs.newClientsThisMonth >= strategicKPIs.newClientsPrevMonth ? '↑' : '↓'} 
                        {' '}vs {strategicKPIs.newClientsPrevMonth} préc.
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-purple-700">{strategicKPIs.returningClients}</p>
                      <p className="text-xs text-purple-600">Clients récurrents</p>
                      <p className="text-xs mt-1 text-gray-500">
                        {strategicKPIs.churnedClients} perdus
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="text-sm text-gray-600">Actifs</span>
                    </div>
                    <span className="font-bold text-gray-800">{strategicKPIs.activeClients}</span>
                  </div>
                </CardContent>
              </Card>
              
              {/* Geographic Distribution */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-[#1db584]" />
                    Répartition Géographique
                  </CardTitle>
                  <CardDescription>Intraland vs Interland</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-indigo-50 rounded-lg p-4 text-center border-2 border-indigo-200">
                      <MapPin className="h-8 w-8 text-indigo-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-indigo-700">{strategicKPIs.intralandCount}</p>
                      <p className="text-xs text-indigo-600 font-medium">INTRALAND</p>
                      <p className="text-sm font-semibold text-indigo-800 mt-1" title={`${formatExact(strategicKPIs.intralandRevenue)} FCFA`}>{formatExact(strategicKPIs.intralandRevenue)} FCFA</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 text-center border-2 border-orange-200">
                      <MapPin className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-orange-700">{strategicKPIs.interlandCount}</p>
                      <p className="text-xs text-orange-600 font-medium">INTERLAND</p>
                      <p className="text-sm font-semibold text-orange-800 mt-1" title={`${formatExact(strategicKPIs.interlandRevenue)} FCFA`}>{formatExact(strategicKPIs.interlandRevenue)} FCFA</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Répartition CA</span>
                      <span>{((strategicKPIs.intralandRevenue / (strategicKPIs.intralandRevenue + strategicKPIs.interlandRevenue || 1)) * 100).toFixed(0)}% / {((strategicKPIs.interlandRevenue / (strategicKPIs.intralandRevenue + strategicKPIs.interlandRevenue || 1)) * 100).toFixed(0)}%</span>
                    </div>
                    <div className="flex h-3 rounded-full overflow-hidden">
                      <div 
                        className="bg-indigo-500 transition-all"
                        style={{ width: `${(strategicKPIs.intralandRevenue / (strategicKPIs.intralandRevenue + strategicKPIs.interlandRevenue || 1)) * 100}%` }}
                      ></div>
                      <div 
                        className="bg-orange-500 transition-all"
                        style={{ width: `${(strategicKPIs.interlandRevenue / (strategicKPIs.intralandRevenue + strategicKPIs.interlandRevenue || 1)) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Row 4: Operational KPIs */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-5 w-5 text-blue-500" />
                <h2 className="text-lg font-semibold text-gray-800">KPIs Opérationnels</h2>
                <Badge variant="outline" className="text-xs">Logistique</Badge>
              </div>
              
              {/* Operational Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                <Card className="bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-sky-600 font-medium">Distance Totale</p>
                        <p className="text-xl font-bold text-sky-700">{formatAmount(strategicKPIs.totalDistanceKm)} km</p>
                        <p className="text-[10px] text-sky-500">Moy: {strategicKPIs.avgDistancePerOp.toFixed(0)} km/op</p>
                      </div>
                      <MapPin className="h-6 w-6 text-sky-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-amber-600 font-medium">Tonnes Transportées</p>
                        <p className="text-xl font-bold text-amber-700">{formatAmount(strategicKPIs.totalWeightTons)} T</p>
                        <p className="text-[10px] text-amber-500">Moy: {strategicKPIs.avgWeightPerOp.toFixed(1)} T/op</p>
                      </div>
                      <Package className="h-6 w-6 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-violet-600 font-medium">Opérations Cumulées</p>
                        <p className="text-xl font-bold text-violet-700">{basicStats.totalOperations}</p>
                        <p className="text-[10px] text-violet-500">{basicStats.operationsTerminees} terminées</p>
                      </div>
                      <BarChart3 className="h-6 w-6 text-violet-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-teal-600 font-medium">Camions Utilisés</p>
                        <p className="text-xl font-bold text-teal-700">{strategicKPIs.totalTrucks}</p>
                        <p className="text-[10px] text-teal-500">Sur {basicStats.totalOperations} ops</p>
                      </div>
                      <Truck className="h-6 w-6 text-teal-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-cyan-600 font-medium">Conteneurs</p>
                        <p className="text-xl font-bold text-cyan-700">{strategicKPIs.totalContainers || 0}</p>
                        <p className="text-[10px] text-cyan-500">Transport maritime</p>
                      </div>
                      <Package className="h-6 w-6 text-cyan-500" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={`bg-gradient-to-br ${strategicKPIs.totalIncidents > 0 ? 'from-red-50 to-red-100 border-red-200' : 'from-green-50 to-green-100 border-green-200'}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-xs font-medium ${strategicKPIs.totalIncidents > 0 ? 'text-red-600' : 'text-green-600'}`}>Incidents</p>
                        <p className={`text-xl font-bold ${strategicKPIs.totalIncidents > 0 ? 'text-red-700' : 'text-green-700'}`}>{strategicKPIs.totalIncidents}</p>
                        <p className={`text-[10px] ${strategicKPIs.totalIncidents > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {strategicKPIs.incidentRate.toFixed(1)}% des ops
                        </p>
                      </div>
                      <AlertTriangle className={`h-6 w-6 ${strategicKPIs.totalIncidents > 0 ? 'text-red-500' : 'text-green-500'}`} />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* Vehicle & Cargo Type Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Vehicle Types */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Truck className="h-5 w-5 text-[#1db584]" />
                      Répartition par Type de Véhicule
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={strategicKPIs.vehicleTypeBreakdown?.slice(0, 8) || []} layout="vertical" margin={{ left: 80 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 10 }} />
                        <YAxis type="category" dataKey="type" tick={{ fontSize: 10 }} width={75} />
                        <Tooltip formatter={(value, name, props) => [`${value} ops (${props.payload.percent?.toFixed(0)}%)`, 'Opérations']} />
                        <Bar dataKey="count" fill="#1db584" radius={[0, 4, 4, 0]}>
                          {(strategicKPIs.vehicleTypeBreakdown || []).slice(0, 8).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                
                {/* Cargo Types */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Package className="h-5 w-5 text-[#1db584]" />
                      Types de Marchandises
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {strategicKPIs.cargoTypeBreakdown?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={strategicKPIs.cargoTypeBreakdown.slice(0, 6)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="count"
                            nameKey="type"
                            label={({ type, percent }) => `${type.slice(0, 15)}${type.length > 15 ? '...' : ''}`}
                            labelLine={false}
                          >
                            {strategicKPIs.cargoTypeBreakdown.slice(0, 6).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [`${value} ops`, name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                          <p>Aucune donnée de marchandise</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>


        {/* Basic KPI Cards - visible only on overview tab */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6 mt-6">
          <KPICard title="Utilisateurs" value={basicStats.totalUsers} icon={Users} color="blue" />
          <KPICard title="Actifs (30j)" value={advancedStats.summary.activeUsers30d} icon={Activity} color="green" />
          <KPICard title="Entreprises" value={advancedStats.summary.totalCompanies} icon={Building} color="purple" />
          <KPICard title="Opérations" value={basicStats.totalOperations} icon={Truck} color="amber" />
          <KPICard title="CA Total" value={`${formatExact(basicStats.revenuTotal)} FCFA`} icon={DollarSign} color="emerald" />
          <KPICard title="Complétion" value={`${basicStats.completionRate}%`} icon={CheckCircle} color="cyan" />
        </div>
        
        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#1db584]" />
                Évolution des opérations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1db584" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#1db584" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis yAxisId="left" tickFormatter={formatAmount} tick={{ fontSize: 10 }} width={50} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={40} />
                  <Tooltip formatter={(value, name) => [name === 'CA' ? `${formatExact(value)} XOF` : value, name]} />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="CA" stroke="#1db584" fill="url(#colorRevenue)" />
                  <Line yAxisId="right" type="monotone" dataKey="operations" name="Opérations" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PieChartIcon className="h-5 w-5 text-[#1db584]" />
                Répartition par statut
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={statusData.filter(d => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={false}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        
        {/* Sector Charts — redesigned for readability */}
        {(() => {
          // Sorted descending, "Non Défini" always last
          const sortedByRevenue = [...advancedStats.bySector]
            .sort((a, b) => {
              if (a.sector === 'Non Defini' || a.sector === 'non_defini') return 1;
              if (b.sector === 'Non Defini' || b.sector === 'non_defini') return -1;
              return b.revenue - a.revenue;
            })
            .slice(0, 10);

          const sortedByOps = [...advancedStats.bySector]
            .sort((a, b) => {
              if (a.sector === 'Non Defini' || a.sector === 'non_defini') return 1;
              if (b.sector === 'Non Defini' || b.sector === 'non_defini') return -1;
              return b.operations - a.operations;
            })
            .slice(0, 10);

          // Abbreviate label for Y axis — full name in tooltip
          const abbreviate = (label) => {
            if (!label) return '';
            const replacements = {
              'Transport & Logistique': 'Transport & Log.',
              'Conseil & Services aux entreprises': 'Services B2B',
              'Industrie legere & Manufacture': 'Industrie & Manuf.',
              'Agroalimentaire & Transformation': 'Agroalimentaire',
              'Cacao, Cafe & Cultures d\'exportation': 'Cacao / Café',
              'Grande distribution & Supermarches': 'Grande distrib.',
              'Telecommunications & IT': 'Télécom & IT',
              'BTP & Construction': 'BTP & Constr.',
              'Mines & Extraction': 'Mines & Extr.',
              'Banque & Finance': 'Banque & Fin.',
              'Import / Export': 'Import/Export',
            };
            return replacements[label] || (label.length > 18 ? label.slice(0, 16) + '…' : label);
          };

          const chartHeight = Math.max(260, sortedByRevenue.length * 38 + 40);

          const CustomRevenueLabel = ({ x, y, width, value, height }) => {
            if (!value || value === 0) return null;
            return (
              <text x={x + width + 6} y={y + height / 2 + 4} fill="#374151" fontSize={11} fontWeight={600}>
                {formatAmount(value)}
              </text>
            );
          };
          const CustomOpsLabel = ({ x, y, width, value, height }) => {
            if (!value || value === 0) return null;
            return (
              <text x={x + width + 6} y={y + height / 2 + 4} fill="#374151" fontSize={11} fontWeight={600}>
                {value.toLocaleString('fr-FR')}
              </text>
            );
          };

          return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* CA par Secteur */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-[#1db584]" />
                    CA par Secteur
                  </CardTitle>
                  <span className="text-xs text-gray-400 font-normal">Trié par CA · Top {sortedByRevenue.length}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={sortedByRevenue.map(d => ({ ...d, shortLabel: abbreviate(d.sectorLabel) }))}
                    layout="vertical"
                    margin={{ top: 4, right: 90, left: 8, bottom: 4 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis
                      type="number"
                      tickFormatter={formatAmount}
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="shortLabel"
                      tick={{ fontSize: 11, fill: '#374151' }}
                      width={110}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatExact(value)} XOF`, 'CA']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.sectorLabel || label}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="revenue" radius={[0, 6, 6, 0]} maxBarSize={24}>
                      {sortedByRevenue.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.sector === 'Non Defini' || entry.sector === 'non_defini'
                            ? '#e5e7eb'
                            : '#1db584'}
                          fillOpacity={entry.sector === 'Non Defini' || entry.sector === 'non_defini' ? 0.7 : (1 - index * 0.07)}
                        />
                      ))}
                      <LabelList content={<CustomRevenueLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Opérations par Secteur */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Package className="h-5 w-5 text-[#3b82f6]" />
                    Opérations par Secteur
                  </CardTitle>
                  <span className="text-xs text-gray-400 font-normal">Trié par volume · Top {sortedByOps.length}</span>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart
                    data={sortedByOps.map(d => ({ ...d, shortLabel: abbreviate(d.sectorLabel) }))}
                    layout="vertical"
                    margin={{ top: 4, right: 60, left: 8, bottom: 4 }}
                    barCategoryGap="30%"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="shortLabel"
                      tick={{ fontSize: 11, fill: '#374151' }}
                      width={110}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(value) => [value.toLocaleString('fr-FR'), 'Opérations']}
                      labelFormatter={(label, payload) => payload?.[0]?.payload?.sectorLabel || label}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="operations" name="Opérations" radius={[0, 6, 6, 0]} maxBarSize={24}>
                      {sortedByOps.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.sector === 'Non Defini' || entry.sector === 'non_defini'
                            ? '#e5e7eb'
                            : '#3b82f6'}
                          fillOpacity={entry.sector === 'Non Defini' || entry.sector === 'non_defini' ? 0.7 : (1 - index * 0.07)}
                        />
                      ))}
                      <LabelList content={<CustomOpsLabel />} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
          );
        })()}
        
        {/* Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#1db584]" />
                Top 10 Entreprises (CA)
              </CardTitle>
              <CardDescription>Classement par chiffre d'affaires</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {advancedStats.topCompanies.map((company, index) => (
                  <div key={company.userId} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm text-gray-800">{company.companyName}</p>
                        <p className="text-xs text-gray-500">{company.sectorLabel} • {company.operations} ops</p>
                      </div>
                    </div>
                    <span className="font-semibold text-sm text-[#1db584]" title={`${formatExact(company.revenue)} FCFA`}>{formatExact(company.revenue)} FCFA</span>
                  </div>
                ))}
                {advancedStats.topCompanies.length === 0 && (
                  <p className="text-center text-gray-500 py-4">Aucune entreprise avec du CA</p>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Entreprises à Faible Activité
              </CardTitle>
              <CardDescription>Moins de 2 opérations en 90 jours</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[280px] overflow-y-auto">
                {advancedStats.lowActivityCompanies.map((company) => (
                  <div key={company.userId} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg border border-amber-100">
                    <div>
                      <p className="font-medium text-sm text-gray-800">{company.companyName || 'Sans nom'}</p>
                      <p className="text-xs text-gray-500">{company.sectorLabel}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      company.operationsLast90Days === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {company.operationsLast90Days} ops
                    </span>
                  </div>
                ))}
                {advancedStats.lowActivityCompanies.length === 0 && (
                  <p className="text-center text-green-600 py-4">✓ Toutes les entreprises sont actives</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

          </TabsContent>
          )}

          {/* Tab: KPIs Opérationnels — lazy mount + forceMount (1 seul fetch sur toute la session) */}
          <TabsContent value="operational" className="mt-6" forceMount={mountedTabs.has('operational') ? true : undefined} hidden={effectiveTab !== 'operational'}>
            {mountedTabs.has('operational') && (
              <OperationalKPIs period={activeFilters.period} token={'cookie-auth'} />
            )}
          </TabsContent>

          {/* Tab: KPIs Financiers */}
          <TabsContent value="financial" className="mt-6" forceMount={mountedTabs.has('financial') ? true : undefined} hidden={effectiveTab !== 'financial'}>
            {mountedTabs.has('financial') && (
              <FinancialKPIs period={activeFilters.period} token={'cookie-auth'} />
            )}
          </TabsContent>

          {/* Tab: Recouvrement */}
          <TabsContent value="recouvrement" className="mt-6" forceMount={mountedTabs.has('recouvrement') ? true : undefined} hidden={effectiveTab !== 'recouvrement'}>
            {mountedTabs.has('recouvrement') && (
              <RecouvrementKPIs token={'cookie-auth'} period={activeFilters.period} />
            )}
          </TabsContent>

          {/* Tab: Objectifs & Relances */}
          <TabsContent value="objectives" className="mt-6" forceMount={mountedTabs.has('objectives') ? true : undefined} hidden={effectiveTab !== 'objectives'}>
            {mountedTabs.has('objectives') && <ObjectivesKPIDashboard />}
          </TabsContent>
          <TabsContent value="profitability" className="mt-6" forceMount={mountedTabs.has('profitability') ? true : undefined} hidden={effectiveTab !== 'profitability'}>
            {mountedTabs.has('profitability') && (
              <ProfitabilityKPIs token={'cookie-auth'} />
            )}
          </TabsContent>
          {canSeeStrategicKPIs && (
          <TabsContent value="fleet" className="mt-6" forceMount={mountedTabs.has('fleet') ? true : undefined} hidden={effectiveTab !== 'fleet'}>
            {mountedTabs.has('fleet') && <FleetKPIs token={'cookie-auth'} />}
          </TabsContent>
          )}
        </Tabs>

      </div>
    </div>
  );
};

export default AdminDashboard;
